import React from 'react'
import {
  DashboardIcon,
  ImportContactsIcon,
  InsertChartIcon,
  MonetizationOnIcon,
  VolunteerActivismIcon,
  SchoolIcon,
  SettingsIcon,
  SupervisedUserCircleIcon,
  OpenInNewIcon,
  Icon,
  HelpIcon,
  HandshakeIcon,
  GroupIcon,
} from '@resiliatech/birdsong'
import { useAuth0 } from '@auth0/auth0-react'

import useCommunityRedirection from 'Hooks/useCommunity/useCommunityRedirection'
import useCommunityGatingRules from 'Hooks/useCommunity/useCommunityGatingRules'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  AUTH0_ACCESS_TOKEN_NAMESPACE,
  AUTH0_CUSTOMER_SUCCESS_ROLE,
} from 'Auth/Constants'
import { analytics } from 'Modules/Analytics'
import { community } from 'Modules/Analytics/trackActionInterface/Community'
import { funderFinder } from 'Modules/Analytics/trackActionInterface/Fundraising/funderFinder'
import { coaching } from 'Modules/Analytics/trackActionInterface/Academy/coaching'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { stories } from 'Modules/Analytics/trackActionInterface/Impact/Stories'
import { home } from 'Modules/Analytics/trackActionInterface/Home'
import { donations } from 'Modules/Analytics/trackActionInterface/Fundraising/donations'
import { user as userActions } from 'Modules/Analytics/trackActionInterface/Account/user'
import { settings } from 'Modules/Analytics/trackActionInterface/Account/settings'
import { billingAndPlan } from 'Modules/Analytics/trackActionInterface/Account/billingAndPlan'
import { hasPermissions } from 'Components/SharedComponents/WithPermission/HasPermission'
import { useEndLoginAsMutation } from 'Generated/graphql-types'

export type SubNavItemConfiguration = Omit<NavItemConfiguration, 'Icon'> & {
  // Whether to hide this sub item on mobile viewports
  hideOnMobile?: boolean
  // A Birdsong trailing icon to display next to the navigation item
  TrailingIcon?: Icon
}

export type NavItemConfiguration = {
  // A unique string to display as the navigation item label
  label: string
  // A Birdsong icon to display next to the navigation item
  Icon?: Icon
  // A list of Auth0 role permissions which are required in order to see this item
  permissions: string[]
  // A list of sub nav items which will open in a dropdown
  subNavItems?: SubNavItemConfiguration[]
  // A list of sub nav items which will open in a sub menu next to the navigation
  subMenuItems?: SubNavItemConfiguration[]
  // Adds a `new` chip to the item
  new?: boolean
  /**
   * Route to navigate to. If the route starts with "http", then this will be a regular link.
   * Otherwise, it will be treated as a route within the app.
   */
  route?: string
  // When route starts with 'http', this will be used to indicate opening the url in a new tab
  spawn?: boolean
  /**
   * For determining if this nav item matches the current route.
   * By default, we'll use the route value as a prefix,
   * but if this covers more than one nav item then you'll want to override the regex.
   */
  routeRegex?: RegExp
  // Config to be passed to `trackActionCreator` when this item is clicked
  eventConfig?: Parameters<typeof analytics.trackActionCreator>[0]
  /**
   * Function that handles the mouse click event to handle any unique behavior
   * for this navigation item
   */
  handleClick?: React.MouseEventHandler
  /**
   * The name of a feature gating rule, if this is provided and the user's plan
   * is not sufficient to use this feature, it will display a chip next to the
   * navigation item with the name of the plan tier they need
   */
  featureGatingRule?: string
  // A Birdsong trailing icon to display next to the navigation item
  TrailingIcon?: Icon
  /**
   * Set this to true to indicate an item that shouldn't do anything when
   * clicked. No event will be tracked on click
   */
  noclick?: boolean
}

/*
 * Configuration for the upper navigation items in the navigation bar.
 * This is a hook, so you may use other hooks to provide data to generate the config.
 */
const useAppNavItems = (): NavItemConfiguration[] => {
  const { communityRedirectionUrl } = useCommunityRedirection()
  const { isUserAllowed } = useCommunityGatingRules()
  const { userConfig } = useAppState()
  const isFunder = userConfig.permissions?.includes('page:funder_dashboard')

  if (isFunder) {
    return [
      {
        label: 'Grantees',
        Icon: HandshakeIcon,
        route: '/funder/grantees',
        permissions: ['page:funder_dashboard'],
      },
      ...(isUserAllowed
        ? [
            {
              label: 'Community',
              Icon: SupervisedUserCircleIcon,
              permissions: ['page:community'],
              route: communityRedirectionUrl,
              eventConfig: community.Clicked,
            },
          ]
        : []),
      {
        label: 'Impact',
        Icon: InsertChartIcon,
        route: '/funder/dashboard',
        permissions: ['page:funder_dashboard'],
      },
      {
        label: 'Goals',
        Icon: InsertChartIcon,
        route: '/funder/impact/goals',
        permissions: ['page:funder_impact:funder_goals'],
      },
      {
        label: 'Stories',
        Icon: ImportContactsIcon,
        route: '/funder/impact/stories/shared',
        permissions: ['page:funder_impact:funder_stories'],
      },
      {
        label: 'Academy',
        Icon: SchoolIcon,
        route: '/academy/all-resources',
        permissions: ['page:academy:all_resources'],
      },
    ]
  }

  return [
    {
      label: 'Dashboard',
      Icon: DashboardIcon,
      route: '/home',
      permissions: ['page:home'],
      eventConfig: home.Clicked,
    },
    {
      label: 'Academy',
      Icon: SchoolIcon,
      route: '/academy/all-resources',
      permissions: ['page:academy:all_resources'],
    },
    {
      label: 'Goals',
      Icon: InsertChartIcon,
      route: '/impact/goals',
      permissions: ['page:impact:initiatives'],
      eventConfig: initiatives.Clicked,
    },
    {
      label: 'Coaching',
      Icon: GroupIcon,
      route: '/coaching',
      permissions: ['page:coaching'],
      eventConfig: coaching.Clicked,
    },
    {
      label: 'Stories',
      Icon: ImportContactsIcon,
      route: '/impact/stories/published',
      permissions: ['page:impact:stories'],
      featureGatingRule: 'Dashboard',
      eventConfig: stories.Clicked,
    },
    ...(isUserAllowed
      ? [
          {
            label: 'Community',
            Icon: SupervisedUserCircleIcon,
            permissions: ['page:community'],
            route: communityRedirectionUrl,
            eventConfig: community.Clicked,
          },
        ]
      : []),
    {
      label: 'Funder Finder',
      Icon: MonetizationOnIcon,
      route: '/fundraising/recommended',
      permissions: ['page:fundraising:funder_finder'],
      featureGatingRule: 'FunderProfileView',
      eventConfig: funderFinder.Clicked,
    },
    {
      label: 'Donations',
      Icon: VolunteerActivismIcon,
      route: '/fundraising/donations',
      permissions: ['page:fundraising:transactions'],
      eventConfig: donations.Clicked,
    },
  ]
}

/*
 * Configuration for the lower navigation items in the navigation bar.
 * This is a hook, so you may use other hooks to provide data to generate the config.
 */
const useAppAccountItems = (): NavItemConfiguration[] => {
  const { userConfig } = useAppState()
  const { user, logout } = useAuth0()
  const auth0Roles = user ? user[`${AUTH0_ACCESS_TOKEN_NAMESPACE}/roles`] : null
  const showCsView = auth0Roles?.includes(AUTH0_CUSTOMER_SUCCESS_ROLE)

  const [endLoginAsMutation] = useEndLoginAsMutation({
    onCompleted: () => {
      localStorage.setItem('ImpersonationSession', '')
      setTimeout(() => {
        // eslint-disable-next-line no-restricted-globals
        location.reload()
      }, 1000)
    },
  })

  return [
    {
      label: 'Help',
      route: 'https://help.resilia.com',
      spawn: true,
      permissions: [],
      TrailingIcon: OpenInNewIcon,
      Icon: HelpIcon,
    },
    {
      label: 'Account',
      Icon: SettingsIcon,
      permissions: ['page:account'],
      subMenuItems: [
        ...(userConfig.email
          ? [
              {
                label: userConfig.email,
                permissions: [],
                hideOnMobile: true,
                noclick: true,
              },
            ]
          : []),
        {
          label: 'Team',
          route: '/funder/team',
          permissions: ['page:funder_dashboard'],
          // eventConfig: team.Clicked, // TODO: Add event config for funder
        },
        {
          label: 'Settings',
          route: hasPermissions(['billing:update'], userConfig.permissions)
            ? '/settings/organization'
            : '/settings/account',
          permissions: ['page:settings'],
          eventConfig: settings.Clicked,
        },
        ...(showCsView
          ? [
              {
                label: 'CS View',
                route: '/team/csview',
                permissions: [],
              },
            ]
          : []),
        {
          label: 'Billing & Plan',
          route: '/settings/billing',
          permissions: ['page:billing', 'billing:update'],
          eventConfig: billingAndPlan.ManageBilling_Clicked,
        },
        {
          label: 'Log Out',
          permissions: [],
          eventConfig: userActions.Signed_Out,
          handleClick: () => {
            const impSession = localStorage.getItem('ImpersonationSession')
            if (impSession) {
              endLoginAsMutation()
            } else {
              const ts = new Date()
              const auth0Id = user ? user.sub : null
              if (auth0Id) {
                // Clear user permissions cache on server
                fetch(
                  `${window.location.protocol}//${window._env_.apiURI}/clear?id=${auth0Id}&ts=${ts}`
                )
              }
              logout({
                logoutParams: {
                  returnTo: window.location.origin,
                },
              })
            }
          },
        },
      ],
    },
  ]
}

export const useAppNavigationConfig = (): {
  navItems: NavItemConfiguration[]
  accountItems: NavItemConfiguration[]
} => {
  const navItems = useAppNavItems()
  const accountItems = useAppAccountItems()

  return {
    navItems,
    accountItems,
  }
}
